import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import SearchBar from '../components/ui/SearchBar';
import DataTable from '../components/ui/DataTable';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import PlanPaymentModal from '../components/modals/PlanPaymentModal';

function PlanPaymentsPage() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [statusFilter, setStatusFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    
    const { hasPermission } = usePermissionContext();

    const storedUser = JSON.parse(sessionStorage.getItem('user'));
    const userRole = storedUser?.role?.toUpperCase();
    const isCompanyAdmin = userRole === 'ADMIN' || userRole === 'COMPANY_ADMIN';
    const companyId = storedUser?.companyId || storedUser?.company_id;

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [companyDetails, setCompanyDetails] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalAmountPaid, setTotalAmountPaid] = useState(0);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: limit,
                status: statusFilter
            });
            if (searchQuery) queryParams.append('search', searchQuery);

            const data = await fetchApi(`/plan-payment?${queryParams.toString()}`);
            setPayments(data.payments || []);
            setTotalPages(data.pages || 1);
            setTotalEntries(data.total || 0);
            setTotalAmountPaid(data.totalAmountPaid || 0);

            // Also fetch stats
            const statsData = await fetchApi('/plan-payment/stats');
            setStats(statsData);
        } catch (error) {
            console.error("Error fetching payments:", error);
            toast.error("Failed to fetch payments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchPayments();
        }, 300);
        return () => clearTimeout(delay);
    }, [currentPage, limit, statusFilter, searchQuery]);

    useEffect(() => {
        if (isCompanyAdmin && companyId) {
            fetchApi(`/company/${companyId}`)
                .then(data => setCompanyDetails(data))
                .catch(err => console.error("Error fetching company details:", err));
        }
    }, [isCompanyAdmin, companyId]);

    const hasVerifiedPayment = payments.some(p => p.paymentStatus === 'Verified');
    const hasPendingPayment = payments.some(p => p.paymentStatus === 'Pending');
    const hasPlan = Boolean(companyDetails?.planId || companyDetails?.plan_id);

    // Check if current plan is expired
    let isExpired = false;
    if (companyDetails?.planExpiryDate) {
        const now = new Date();
        const expiry = new Date(companyDetails.planExpiryDate);
        isExpired = now > expiry;
    } else if (hasPlan) {
        isExpired = true;
    }

    // Disable if: no plan assigned, OR has pending payment, OR (has verified payment AND not expired)
    const isPayDisabled = !hasPlan || hasPendingPayment || (hasVerifiedPayment && !isExpired);

    const getButtonTitle = () => {
        if (!hasPlan) return "No plan assigned to your company";
        if (hasPendingPayment) return "Payment is pending verification by admin";
        if (hasVerifiedPayment && !isExpired) return "Payment is already verified for your active plan";
        if (isExpired) return "Plan has expired. Click to pay & renew your subscription.";
        return "Pay for your selected plan";
    };

    const getButtonText = () => {
        if (!hasPlan) return "No Plan Assigned";
        if (hasPendingPayment) return "Payment Pending";
        if (hasVerifiedPayment && !isExpired) return "Payment Verified";
        if (isExpired) return "Renew Plan";
        return "Pay for Plan";
    };

    const handleUpdateStatus = async (id, newStatus) => {
        let rejectionReason = null;

        if (newStatus === 'Rejected') {
            const { value: text } = await Swal.fire({
                title: 'Reject Payment',
                input: 'textarea',
                inputLabel: 'Reason for rejection',
                inputPlaceholder: 'Enter the reason...',
                showCancelButton: true,
                inputValidator: (value) => {
                    if (!value) {
                        return 'You need to write something!'
                    }
                }
            });

            if (!text) return;
            rejectionReason = text;
        } else {
            const result = await Swal.fire({
                title: `Mark as ${newStatus}?`,
                text: "Are you sure you want to verify this payment?",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, verify it!'
            });

            if (!result.isConfirmed) return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/plan-payment/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    payment_status: newStatus,
                    rejection_reason: rejectionReason
                })
            });

            if (response.ok) {
                toast.success(`Payment marked as ${newStatus}`);
                fetchPayments(); // Refresh list
            } else {
                const errData = await response.json();
                toast.error(errData.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Update status error:', error);
            toast.error('An error occurred');
        }
    };

    const handleEditPayment = async (row) => {
        const { value: formValues } = await Swal.fire({
            title: 'Edit Payment',
            html: `
                <div style="text-align: left;">
                    <label style="font-size: 14px; color: #475569; margin-bottom: 4px; display: block;">Amount Paid</label>
                    <input id="swal-amount" type="number" class="swal2-input" style="width: 100%; margin: 0 0 15px 0; max-width: 100%; box-sizing: border-box;" value="${row.amountPaid}" />
                    
                    <label style="font-size: 14px; color: #475569; margin-bottom: 4px; display: block;">Payment Mode</label>
                    <select id="swal-mode" class="swal2-input" style="width: 100%; margin: 0 0 15px 0; max-width: 100%; box-sizing: border-box;">
                        <option value="Online" ${row.paymentMode === 'Online' ? 'selected' : ''}>Online</option>
                        <option value="Bank Transfer" ${row.paymentMode === 'Bank Transfer' ? 'selected' : ''}>Bank Transfer</option>
                        <option value="UPI" ${row.paymentMode === 'UPI' ? 'selected' : ''}>UPI</option>
                        <option value="Cash" ${row.paymentMode === 'Cash' ? 'selected' : ''}>Cash</option>
                        <option value="Cheque" ${row.paymentMode === 'Cheque' ? 'selected' : ''}>Cheque</option>
                        <option value="Other" ${row.paymentMode === 'Other' ? 'selected' : ''}>Other</option>
                    </select>

                    <label style="font-size: 14px; color: #475569; margin-bottom: 4px; display: block;">Transaction ID</label>
                    <input id="swal-transaction" class="swal2-input" style="width: 100%; margin: 0 0 15px 0; max-width: 100%; box-sizing: border-box;" value="${row.transactionId || ''}" />
                    
                    <label style="font-size: 14px; color: #475569; margin-bottom: 4px; display: block;">Payment Note</label>
                    <textarea id="swal-note" class="swal2-textarea" style="width: 100%; margin: 0; max-width: 100%; box-sizing: border-box;">${row.paymentNote || ''}</textarea>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Save Changes',
            preConfirm: () => {
                const amount = document.getElementById('swal-amount').value;
                const mode = document.getElementById('swal-mode').value;
                const transaction = document.getElementById('swal-transaction').value;
                const note = document.getElementById('swal-note').value;
                
                if (!amount) {
                    Swal.showValidationMessage('Amount Paid is required');
                }
                return { amount_paid: amount, payment_mode: mode, transaction_id: transaction, payment_note: note };
            }
        });

        if (formValues) {
            try {
                const response = await fetch(`${API_BASE_URL}/plan-payment/${row._id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                    },
                    body: JSON.stringify(formValues)
                });
                
                if (response.ok) {
                    toast.success('Payment updated successfully');
                    fetchPayments();
                } else {
                    const err = await response.json();
                    toast.error(err.message || 'Failed to update payment');
                }
            } catch (error) {
                console.error('Update payment error:', error);
                toast.error('An error occurred');
            }
        }
    };

    const handleDeletePayment = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${API_BASE_URL}/plan-payment/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${sessionStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    toast.success('Payment deleted successfully');
                    fetchPayments();
                } else {
                    const err = await response.json();
                    toast.error(err.message || 'Failed to delete payment');
                }
            } catch (error) {
                console.error('Delete payment error:', error);
                toast.error('An error occurred');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Verified': return 'bg-success';
            case 'Rejected': return 'bg-danger';
            default: return 'bg-warning text-dark';
        }
    };

    const columns = [
        { key: 'createdAt', label: 'DATE', render: (row) => formatDate(row.createdAt) },
        { key: 'companyName', label: 'COMPANY', render: (row) => <span className="fw-semibold text-primary">{row.companyName}</span> },
        {
            key: 'planName', label: 'PLAN & PRICE', render: (row) => (
                <>
                    <div>{row.planName}</div>
                    <small className="text-muted">Expected: ₹{row.planFinalPrice}</small>
                </>
            )
        },
        { key: 'amountPaid', label: 'AMOUNT PAID', render: (row) => <span className="fw-bold text-success">₹{row.amountPaid}</span> },
        {
            key: 'paymentMode', label: 'MODE & REF', render: (row) => (
                <>
                    <div>{row.paymentMode}</div>
                    {row.transactionId && <small className="text-muted d-block">{row.transactionId}</small>}
                    {row.couponCode && <small className="badge bg-light text-dark border mt-1">Code: {row.couponCode}</small>}
                </>
            )
        },
        {
            key: 'paymentStatus', label: 'STATUS', render: (row) => (
                <span className={`badge ${getStatusBadgeClass(row.paymentStatus)}`}>
                    {row.paymentStatus}
                </span>
            )
        },
        {
            key: 'actions', label: 'ACTIONS', render: (row) => (
                <div className="list-page-action-icons justify-content-center">
                    {row.paymentStatus === 'Pending' ? (
                        <>
                            <button
                                className="list-page-icon-btn btn-success p-1"
                                style={{ backgroundColor: '#22c55e', color: 'white', borderRadius: '4px', border: 'none', marginRight: '4px' }}
                                onClick={() => handleUpdateStatus(row._id, 'Verified')}
                                title="Verify"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            </button>
                            <button
                                className="list-page-icon-btn btn-danger p-1 ms-1"
                                style={{ backgroundColor: '#ef4444', color: 'white', borderRadius: '4px', border: 'none', marginRight: '4px' }}
                                onClick={() => handleUpdateStatus(row._id, 'Rejected')}
                                title="Reject"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </>
                    ) : (
                        <small className="text-muted" style={{ marginRight: '8px' }}>
                            By: {row.verifiedByUserName || '—'}
                        </small>
                    )}

                    {hasPermission('Plan Payment', 'update') && (
                        <button type="button" className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(row)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    
                    {hasPermission('Plan Payment', 'delete') && (
                        <button type="button" className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(row._id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header border-bottom-0 pb-0">
                <h1 className="list-page-title mb-0">Plan Payments</h1>
                <div className="list-page-action-buttons d-flex align-items-center gap-3">
                    {isCompanyAdmin && (
                        <button
                            type="button"
                            className="btn d-flex align-items-center gap-2 fw-semibold shadow-sm text-white"
                            style={{
                                backgroundColor: isPayDisabled ? '#94a3b8' : '#0284c7',
                                borderColor: isPayDisabled ? '#94a3b8' : '#0284c7',
                                cursor: isPayDisabled ? 'not-allowed' : 'pointer',
                                padding: '6px 16px',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                            disabled={isPayDisabled}
                            onClick={() => setIsPaymentModalOpen(true)}
                            title={getButtonTitle()}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                            {getButtonText()}
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4 mt-3 mx-2">
                {/* Total Payments */}
                <div className="col-md-3">
                    <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', borderBottom: '3px solid #0ea5e9', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div className="card-body position-relative p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h3 className="fw-bold mb-0" style={{ color: '#0f172a', fontSize: '24px' }}>{stats.totalPayments || 0}</h3>
                                    <div style={{ color: '#475569', fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>Total Payments</div>
                                </div>
                                <div style={{ color: '#0ea5e9' }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pending Verification */}
                <div className="col-md-3">
                    <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', borderBottom: '3px solid #f59e0b', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div className="card-body position-relative p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h3 className="fw-bold mb-0" style={{ color: '#0f172a', fontSize: '24px' }}>{stats.pendingCount || 0}</h3>
                                    <div style={{ color: '#475569', fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>Pending Verification</div>
                                    <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>₹{stats.pendingRevenue || 0} Expected</div>
                                </div>
                                <div style={{ color: '#f59e0b' }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Verified Payments */}
                <div className="col-md-3">
                    <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', borderBottom: '3px solid #22c55e', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div className="card-body position-relative p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h3 className="fw-bold mb-0" style={{ color: '#0f172a', fontSize: '24px' }}>{stats.verifiedCount || 0}</h3>
                                    <div style={{ color: '#475569', fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>Verified Payments</div>
                                    <div style={{ color: '#16a34a', fontSize: '12px', marginTop: '2px', fontWeight: '600' }}>₹{stats.totalRevenue || 0} Collected</div>
                                </div>
                                <div style={{ color: '#22c55e' }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Rejected Payments */}
                <div className="col-md-3">
                    <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', borderBottom: '3px solid #ef4444', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', backgroundColor: '#fff' }}>
                        <div className="card-body position-relative p-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h3 className="fw-bold mb-0" style={{ color: '#0f172a', fontSize: '24px' }}>{stats.rejectedCount || 0}</h3>
                                    <div style={{ color: '#475569', fontSize: '14px', fontWeight: '500', marginTop: '2px' }}>Rejected Payments</div>
                                </div>
                                <div style={{ color: '#ef4444' }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={payments}
                loading={loading}
                emptyMessage="No payments found"
                page={currentPage}
                pageSize={limit}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setLimit}
                searchBar={
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-3">
                        <SearchBar
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search by company or transaction ID..."
                        />
                        <div className="d-flex align-items-center gap-2">
                            <label className="fw-semibold text-secondary mb-0" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Filter Status:</label>
                            <select
                                className="form-select form-select-sm"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{ width: '150px', borderRadius: '6px', height: '38px' }}
                            >
                                <option value="All">All</option>
                                <option value="Pending">Pending</option>
                                <option value="Verified">Verified</option>
                                <option value="Rejected">Rejected</option>
                            </select>
                        </div>
                    </div>
                }
                footerRow={
                    <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
                        <td colSpan="3" className="text-end pe-4" style={{ textAlign: 'right' }}>Total Amount Paid:</td>
                        <td className="text-success">₹{Number(totalAmountPaid).toFixed(2)}</td>
                        <td colSpan="3"></td>
                    </tr>
                }
            />

            {isPaymentModalOpen && (
                <PlanPaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => setIsPaymentModalOpen(false)}
                    companyId={companyId}
                    planId={companyDetails?.planId || companyDetails?.plan_id}
                    planName={companyDetails?.planName || companyDetails?.Plan?.plan_name}
                    finalPrice={companyDetails?.finalPrice || companyDetails?.Plan?.final_price}
                    planPrice={companyDetails?.planPrice || companyDetails?.Plan?.price}
                    planDiscount={companyDetails?.planDiscount}
                    defaultCouponCode={companyDetails?.couponCode}
                    onSuccess={() => {
                        setIsPaymentModalOpen(false);
                        fetchPayments();
                    }}
                />
            )}
        </div>
    );
}

export default PlanPaymentsPage;
